import AccessTimeIcon from "@mui/icons-material/AccessTime";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditCalendarOutlinedIcon from "@mui/icons-material/EditCalendarOutlined";
import {
  alpha,
  Box,
  IconButton,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme
} from "@mui/material";
import moment from "moment-timezone";
import { useMemo } from "react";
import { DefaultButton } from "../../../components/form";
import { FormHelperTextError } from "../../../components/form/errors";
import FlexBox from "../../../components/layouts/Flexbox";
import { APP_COLORS } from "../../../enums";
import {
  concatSelectedFromToTime
} from "../../../services/utils/date-and-time.utils";
import { VendorSlot } from "../vendor-slot-selection/types";

interface AmbassadorManualSlotsSectionProps {
  slots: VendorSlot[];
  browserTimezone: string;
  browserTimezoneAbbr?: string;
  clientTimezoneAbbr?: string;
  clientTimezoneValue?: string;
  onAddSlot: () => void;
  onEditSlot: (index: number) => void;
  onRemoveSlot: (index: number) => void;
  showErrors: boolean;
}

function groupSlotsByDate(slots: VendorSlot[]) {
  return slots.reduce<Record<string, { slot: VendorSlot; index: number }[]>>(
    (acc, slot, index) => {
      if (!slot.selectedSlotTimeFrom || !slot.selectedSlotTimeTo) {
        return acc;
      }

      const dateKey = moment(slot.selectedSlotTimeFrom).format("YYYY-MM-DD");

      if (!acc[dateKey]) {
        acc[dateKey] = [];
      }

      acc[dateKey].push({ slot, index });
      return acc;
    },
    {}
  );
}

export function AmbassadorManualSlotsSection({
  slots,
  browserTimezone,
  browserTimezoneAbbr,
  clientTimezoneAbbr,
  clientTimezoneValue,
  onAddSlot,
  onEditSlot: _onEditSlot,
  onRemoveSlot,
  showErrors,
}: AmbassadorManualSlotsSectionProps) {
  const groupedSlots = useMemo(() => groupSlotsByDate(slots), [slots]);
  const slotCount = useMemo(
    () =>
      slots.filter(
        (slot) => slot.selectedSlotTimeFrom && slot.selectedSlotTimeTo
      ).length,
    [slots]
  );
  const theme = useTheme();
  const isNonDesktop = useMediaQuery(theme.breakpoints.down("lg"));
  const userTimezoneAbbr = useMemo(() => {
    return (
      browserTimezoneAbbr || moment.tz(new Date(), browserTimezone).format("z")
    );
  }, [browserTimezone, browserTimezoneAbbr]);

  const hasSlots = Object.keys(groupedSlots).length > 0;

  return (
    <Box>
      <Typography variant="subtitle2" fontWeight={600} mb={0.5}>
        Provide your availability
      </Typography>
      <Typography variant="caption" color="text.secondary">
        Add at least 3 alternative slots. Times reflect your timezone (
        {userTimezoneAbbr || "N/A"}) and are automatically converted to the
        client&apos;s timezone ({clientTimezoneAbbr || "N/A"}).
      </Typography>
      <FlexBox gap={1} alignItems="center" my={2}>
        {hasSlots && (
          <DefaultButton
            startIcon={<EditCalendarOutlinedIcon fontSize="small" />}
            onClick={onAddSlot}
            fullWidth
          >
            Update Availability
          </DefaultButton>
        )}
      </FlexBox>

      {hasSlots ? (
        <Stack gap={1}>

          <FlexBox gap={1} px={1}>

            <Box width={58} />
            <Box flex={1}>
              <FlexBox alignItems="center" gap={0.5}>
                <AccessTimeIcon
                  sx={{ fontSize: 16, color: "text.secondary" }}
                />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Your Time ({userTimezoneAbbr || "N/A"})
                </Typography>
              </FlexBox>
            </Box>
            <Box flex={1}>
              <FlexBox alignItems="center" gap={0.5}>
                <AccessTimeIcon
                  sx={{ fontSize: 16, color: "text.secondary" }}
                />
                <Typography variant="body2" fontWeight={600}>
                  Client ({clientTimezoneAbbr || "GMT"})
                </Typography>
              </FlexBox>
            </Box>
          </FlexBox>

          {Object.entries(groupedSlots)
            .sort(
              ([dateA], [dateB]) =>
                new Date(dateA).getTime() - new Date(dateB).getTime()
            )
            .map(([dateKey, slotDetails]) => {
              const dayNumber = moment(dateKey, "YYYY-MM-DD").format("D");
              const dayName = moment(dateKey, "YYYY-MM-DD").format("ddd");

              return (
                <FlexBox
                  key={dateKey}
                  gap={1}
                  alignItems="center"
                  bgcolor={APP_COLORS.greyLight}
                  p={1.5}
                  borderRadius={4}
                >

                  <Box
                    width={58}
                    height={58}
                    borderRadius={2}
                    bgcolor={APP_COLORS.white}
                    border={`1px solid ${APP_COLORS.greyLight3}`}
                    p={2}
                    display="flex"
                    flexDirection="column"
                    alignItems="center"
                    justifyContent="center"
                    alignSelf="flex-start"
                    textAlign="center"
                  >
                    <Typography variant="xlarge-bold">{dayNumber}</Typography>
                    <Typography
                      variant="body-medium"
                      color="text.secondary"
                    >
                      {dayName}
                    </Typography>
                  </Box>


                  <Stack gap={1} flex={1}>
                    {[...slotDetails]
                      .sort((a, b) => {
                        const timeA = new Date(
                          a.slot.selectedSlotTimeFrom
                        ).getTime();
                        const timeB = new Date(
                          b.slot.selectedSlotTimeFrom
                        ).getTime();
                        return timeA - timeB;
                      })
                      .map(({ slot, index: originalIndex }) => {
                        const slotDisplayEnd = moment(
                          slot.selectedSlotTimeFrom
                        ).add(45, "minutes");

                        const userTimeRange = concatSelectedFromToTime(
                          {
                            from: slot.selectedSlotTimeFrom,
                            to: slotDisplayEnd.toDate(),
                          },
                          true
                        );

                        const formatClientTime = (
                          value?: Date | string
                        ): { time: string | null; date: string | null } => {
                          if (!value || !clientTimezoneValue)
                            return { time: null, date: null };
                          try {
                            const date = moment(value);
                            if (!date.isValid())
                              return { time: null, date: null };


                            const dateInClientTz = date.tz(clientTimezoneValue);
                            const time = dateInClientTz.format("hh:mm A");
                            const dateStr = dateInClientTz.format("MMM D");

                            return { time, date: dateStr };
                          } catch {
                            return { time: null, date: null };
                          }
                        };

                        const clientFromInfo = formatClientTime(
                          slot.selectedSlotTimeFrom
                        );
                        const clientToInfo = formatClientTime(
                          slotDisplayEnd.toDate()
                        );



                        const userStartDateStr = moment(
                          slot.selectedSlotTimeFrom
                        ).format("YYYY-MM-DD");
                        const userEndDateStr =
                          slotDisplayEnd.format("YYYY-MM-DD");
                        const clientStartDateStr = clientTimezoneValue
                          ? moment(slot.selectedSlotTimeFrom)
                              .tz(clientTimezoneValue)
                              .format("YYYY-MM-DD")
                          : userStartDateStr;
                        const clientEndDateStr = clientTimezoneValue
                          ? slotDisplayEnd
                              .clone()
                              .tz(clientTimezoneValue)
                              .format("YYYY-MM-DD")
                          : userEndDateStr;


                        const userStartMoment = moment(userStartDateStr);
                        const clientStartMoment = moment(clientStartDateStr);
                        const userEndMoment = moment(userEndDateStr);
                        const clientEndMoment = moment(clientEndDateStr);

                        const startDayDiff = clientStartMoment.diff(
                          userStartMoment,
                          "days"
                        );
                        const endDayDiff = clientEndMoment.diff(
                          userEndMoment,
                          "days"
                        );


                        let clientTimeRange = "Conversion unavailable";
                        let startDayIndicator: {
                          text: string;
                          isNegative: boolean;
                        } | null = null;
                        let endDayIndicator: {
                          text: string;
                          isNegative: boolean;
                        } | null = null;

                        if (clientFromInfo.time && clientToInfo.time) {
                          const getDayIndicator = (dayDiff: number) => {
                            if (dayDiff === 0) return null;
                            if (dayDiff < 0) {
                              return {
                                text: `(-${Math.abs(dayDiff)} day${
                                  Math.abs(dayDiff) > 1 ? "s" : ""
                                }) `,
                                isNegative: true,
                              };
                            }
                            return {
                              text: `(+${dayDiff} day${
                                dayDiff > 1 ? "s" : ""
                              }) `,
                              isNegative: false,
                            };
                          };


                          if (
                            startDayDiff === endDayDiff &&
                            startDayDiff !== 0
                          ) {
                            startDayIndicator = getDayIndicator(startDayDiff);
                            clientTimeRange = `${clientFromInfo.time} - ${clientToInfo.time}`;
                          } else {
                            startDayIndicator = getDayIndicator(startDayDiff);
                            endDayIndicator = getDayIndicator(endDayDiff);
                            clientTimeRange = `${clientFromInfo.time} - ${clientToInfo.time}`;
                          }
                        }

                        return (
                          <Paper
                            key={`${dateKey}-${slot.selectedSlotTimeFrom}`}
                            elevation={0}
                            sx={{
                              borderRadius: 3,
                              backgroundColor: APP_COLORS.white,
                              border: `1px solid ${APP_COLORS.greyLight3}`,
                              px: 1.5,
                            }}
                          >
                            {isNonDesktop ? (
                              <Stack gap={1} width="100%">
                                <FlexBox alignItems="flex-start" gap={2} width="100%">
                                  <Box flex={1} minWidth={0}>
                                    <Typography
                                    fontWeight={500}
                                    fontSize="12px"
                                    >
                                      {userTimeRange}
                                    </Typography>
                                    {showErrors && slot.error && (
                                      <FormHelperTextError error={slot.error} />
                                    )}
                                  </Box>
                                  <Box flex={1} minWidth={0}>
                                    <Typography
                                      fontWeight={500}
                                      fontSize="12px"
                                      color={
                                        clientFromInfo.time && clientToInfo.time
                                          ? "text.primary"
                                          : "text.secondary"
                                      }
                                    >
                                      {clientTimeRange}
                                    </Typography>
                                    {(startDayIndicator || endDayIndicator) && (
                                      <Box
                                        sx={{
                                          display: "flex",
                                        }}
                                      >
                                        {startDayIndicator && (
                                          <Typography
                                            component="span"
                                            fontWeight={500}
                                            fontSize="12px"
                                            color={startDayIndicator.isNegative
                                              ? "error.main"
                                              : "success.main"}
                                          >
                                            {startDayIndicator.text}
                                          </Typography>
                                        )}
                                        {endDayIndicator &&
                                          startDayIndicator?.text !==
                                            endDayIndicator?.text && (
                                            <Typography
                                              component="span"
                                              fontWeight={500}
                                              fontSize="12px"
                                              color={endDayIndicator.isNegative
                                                ? "error.main"
                                                : "success.main"}
                                            >
                                              {endDayIndicator.text}
                                            </Typography>
                                          )}
                                      </Box>
                                    )}
                                  </Box>
                                </FlexBox>
                                <FlexBox justifyContent="flex-end" gap={0.5}>
                                  <IconButton
                                    size="small"
                                    onClick={() => onRemoveSlot(originalIndex)}
                                    aria-label="Delete slot"
                                    sx={{
                                      padding: 0,
                                      width: 32,
                                      height: 32,
                                      color: "error.main",
                                      "&:hover": {
                                        backgroundColor: alpha(
                                          theme.palette.error.main,
                                          0.1
                                        ),
                                      },
                                    }}
                                  >
                                    <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </FlexBox>
                              </Stack>
                            ) : (
                              <FlexBox alignItems="center" gap={2} width="100%">
                                <Box flex={1} minWidth={0}>
                                  <Typography
                                    fontWeight={500}
                                    fontSize="12px"
                                  >
                                    {userTimeRange}
                                  </Typography>
                                  {showErrors && slot.error && (
                                    <FormHelperTextError error={slot.error} />
                                  )}
                                </Box>
                                <Box flex={1} minWidth={0}>
                                  <Typography
                                    fontWeight={500}
                                    fontSize="12px"
                                    color={
                                      clientFromInfo.time && clientToInfo.time
                                        ? "text.primary"
                                        : "text.secondary"
                                    }
                                  >
                                    {clientTimeRange}
                                  </Typography>
                                  {(startDayIndicator || endDayIndicator) && (
                                    <Box
                                      sx={{
                                        display: "flex",
                                      }}
                                    >
                                      {startDayIndicator && (
                                        <Typography
                                          component="span"
                                          fontWeight={500}
                                          fontSize="12px"
                                          color={startDayIndicator.isNegative
                                            ? "error.main"
                                            : "success.main"}
                                        >
                                          {startDayIndicator.text}
                                        </Typography>
                                      )}
                                      {endDayIndicator &&
                                        startDayIndicator?.text !==
                                          endDayIndicator?.text && (
                                          <Typography
                                            component="span"
                                            fontWeight={500}
                                            fontSize="12px"
                                            color={endDayIndicator.isNegative
                                              ? "error.main"
                                              : "success.main"}
                                          >
                                            {endDayIndicator.text}
                                          </Typography>
                                        )}
                                    </Box>
                                  )}
                                </Box>
                                <FlexBox gap={0.5}>
                                  <IconButton
                                    size="small"
                                    onClick={() => onRemoveSlot(originalIndex)}
                                    aria-label="Delete slot"
                                    sx={{
                                      padding: 0,
                                      width: 32,
                                      height: 32,
                                      color: "error.main",
                                      "&:hover": {
                                        backgroundColor: alpha(
                                          theme.palette.error.main,
                                          0.1
                                        ),
                                      },
                                    }}
                                  >
                                    <DeleteOutlineIcon sx={{ fontSize: 18 }} />
                                  </IconButton>
                                </FlexBox>
                              </FlexBox>
                            )}
                          </Paper>
                        );
                      })}
                  </Stack>
                </FlexBox>
              );
            })}
        </Stack>
      ) : (
        <DefaultButton
          startIcon={<AddCircleIcon fontSize="small" />}
          onClick={onAddSlot}
          fullWidth
        >
          Add Availability
        </DefaultButton>
      )}

      <Box mt={1.5}>
        {slotCount > 0 && (
          <Typography variant="caption" color="text.secondary">
            You have added {slotCount} slot{slotCount === 1 ? "" : "s"}.
          </Typography>
        )}
      </Box>
    </Box>
  );
}
